import React from 'react';
import { smoothDnD as container, ContainerOptions, SmoothDnD } from 'smooth-dnd';
import { dropHandlers } from 'smooth-dnd';
import { SerializedStyles } from '@emotion/react';
import { useDeepCompareMemo } from '$/hooks/useHasChanged';
import useRunOnce from '$/hooks/useRunOnce';

container.dropHandler = dropHandlers.reactDropHandler().handler;
container.wrapChild = false;

interface ContainerProps extends ContainerOptions {
  children: React.ReactNode;
  offsetCss?: SerializedStyles;
}

const DragContainer: React.FC<ContainerProps> = ({ children, offsetCss, ...otherProps }) => {
  const props = useDeepCompareMemo(otherProps);

  const [prevContainer, setPrevContainer] = React.useState<HTMLElement | null>(null);
  const [dragContainer, setDragContainer] = React.useState<SmoothDnD | null>(null);
  const containerRef = React.createRef<HTMLDivElement>();

  const getContainerOptions = React.useCallback((): ContainerOptions => {
    return Object.keys(props).reduce((result: ContainerOptions, key: string) => {
      const optionName = key as keyof ContainerOptions;
      const prop = props[optionName];

      if (typeof prop === 'function') {
        //@ts-ignore
        result[optionName] = (...params: unknown[]) => {
          // eslint-disable-next-line @typescript-eslint/ban-types
          return (props[optionName] as Function)(...params);
        };
      } else {
        //@ts-ignore
        result[optionName] = prop;
      }

      return result;
    }, {}) as ContainerOptions;
  }, [props]);

  const isObjectTypePropsChanged = React.useCallback(
    (prevProps: ContainerOptions): boolean => {
      for (const k in props) {
        const key = k as keyof ContainerOptions;
        if (Object.prototype.hasOwnProperty.call(props, key)) {
          const prop = props[key];

          if (typeof prop !== 'function' && prop !== prevProps[key]) {
            return true;
          }
        }
      }

      return false;
    },
    [props]
  );

  useRunOnce(() => {
    if (!containerRef.current) return;

    setPrevContainer(containerRef.current);
    setDragContainer(container(containerRef.current, getContainerOptions()));
    return () => {
      dragContainer && dragContainer.dispose();
      setDragContainer(null);
    };
  });

  React.useEffect(() => {
    if (!containerRef.current) return;

    if (prevContainer && prevContainer !== containerRef.current) {
      dragContainer && dragContainer.dispose();
      setDragContainer(container(containerRef.current, getContainerOptions()));
      setPrevContainer(containerRef.current);
      return;
    }

    if (dragContainer && isObjectTypePropsChanged(props)) {
      dragContainer.setOptions(getContainerOptions());
    }
  }, [
    containerRef,
    dragContainer,
    getContainerOptions,
    isObjectTypePropsChanged,
    prevContainer,
    props,
  ]);

  return (
    <div css={offsetCss} ref={containerRef}>
      {children}
    </div>
  );
};

export default DragContainer;
